import {cn} from '@tempmee/core'
import type React from 'react'
import {
  type CSSProperties,
  type HTMLAttributes,
  type ReactNode,
  createContext,
  forwardRef,
  useContext,
} from 'react'
import {TypographyVariantsFactory} from './Variants'

/** MUI-aligned type scale. Preferred going forward. */
export type TypographyVariant =
  | 'h1'
  | 'h2'
  | 'h3'
  | 'h4'
  | 'h5'
  | 'h6'
  | 'subtitle1'
  | 'subtitle2'
  | 'body1'
  | 'body2'
  | 'bodyLarge'
  | 'caption'
  | 'overline'

/** @deprecated Legacy GoTu scale. Use the `variant` prop instead. */
export type TypographySize =
  | 'heading_1'
  | 'heading_2'
  | 'heading_3'
  | 'heading_4'
  | 'body_large'
  | 'body_large_tight'
  | 'body_small'
  | 'caption'
  | 'caption_sm'
  | 'label'

/** @deprecated Use the `bold` boolean prop instead. Only 'regular' and 'bold' are supported going forward. */
export type TypographyWeight = 'regular' | 'bold'

export type TypographyAlign = 'left' | 'center' | 'right' | 'justify'

type TypographyAs = 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6' | 'p' | 'span' | 'label' | 'div'

/** Default HTML element per variant (MUI's variantMapping). Overridable via `as`. */
const VARIANT_ELEMENT: Record<TypographyVariant, TypographyAs> = {
  h1: 'h1',
  h2: 'h2',
  h3: 'h3',
  h4: 'h4',
  h5: 'h5',
  h6: 'h6',
  subtitle1: 'h6',
  subtitle2: 'h6',
  body1: 'p',
  body2: 'p',
  bodyLarge: 'p',
  caption: 'span',
  overline: 'span',
}

const NUMBER_OF_LINES_CLASSES: Record<number, string> = {
  1: 'truncate',
  2: 'line-clamp-2',
  3: 'line-clamp-3',
  4: 'line-clamp-4',
  5: 'line-clamp-5',
  6: 'line-clamp-6',
}

/** True when rendered inside a parent <Typography> — used to skip the default color so the parent's color inherits. */
const TypographyContext = createContext(false)

export interface TypographyProps extends Omit<HTMLAttributes<HTMLElement>, 'children'> {
  /**
   * MUI type-scale variant (h1–h6, subtitle1/2, body1/2, bodyLarge, caption, overline).
   * Headings are responsive (smaller on mobile, full size at `md:`). Takes
   * precedence over the legacy `size` prop. @default 'body1' (when neither set)
   */
  variant?: TypographyVariant
  /** @deprecated Legacy GoTu scale. Use `variant` instead. */
  size?: TypographySize
  /** Forces bold (700), overriding the variant's intrinsic weight. @default false */
  bold?: boolean
  /**
   * @deprecated Use the `bold` boolean prop instead.
   * Only 'regular' and 'bold' are supported. 'semibold' and 'extrabold' will map to 'bold'.
   */
  weight?: TypographyWeight
  /** Renders italic text. @default false */
  italic?: boolean
  /** Renders uppercase text. @default false */
  uppercase?: boolean
  /** Clamps text after N lines with ellipsis. 1 → `truncate`; 2–6 → `line-clamp-N`. */
  numberOfLines?: 1 | 2 | 3 | 4 | 5 | 6
  /** HTML element to render as. Defaults to the variant's semantic element, else 'p'. */
  as?: TypographyAs
  /** Text alignment. @default undefined (inherits CSS text-align) */
  textAlign?: TypographyAlign
  /**
   * Any valid CSS color value. Overrides the default text color.
   * Accepts hex (`#d00019`), CSS variables, named colors, etc.
   */
  color?: CSSProperties['color']
  children: ReactNode
}

export const Typography = forwardRef<HTMLElement, TypographyProps>(
  (
    {
      className,
      style,
      variant,
      size,
      bold,
      weight,
      italic = false,
      uppercase = false,
      numberOfLines,
      as,
      textAlign,
      color,
      children,
      ...props
    },
    ref,
  ) => {
    const isNested = useContext(TypographyContext)
    // bold prop takes precedence; weight is the legacy fallback.
    const isBold = bold ?? weight === 'bold'

    // New `variant` API takes precedence; otherwise fall back to the legacy `size`.
    const useVariant = variant != null
    const Tag = as ?? (useVariant ? VARIANT_ELEMENT[variant] : 'p')

    // Default text color: text-primary for the MUI scale, neutral-600 for the
    // legacy scale (so un-migrated components keep their original color).
    // Skipped when nested (inherits parent) or when an explicit color is given.
    const defaultColorClass =
      !isNested && color == null
        ? useVariant
          ? 'text-text-primary'
          : 'text-neutral-600'
        : undefined

    return (
      <TypographyContext.Provider value={true}>
        <Tag
          ref={ref as React.RefObject<never>}
          className={cn(
            TypographyVariantsFactory({
              variant: useVariant ? variant : null,
              size: useVariant ? null : (size ?? 'body_large'),
              bold: isBold,
              italic,
              uppercase,
              textAlign,
            }),
            defaultColorClass,
            numberOfLines != null && NUMBER_OF_LINES_CLASSES[numberOfLines],
            className,
          )}
          style={color != null ? {color, ...style} : style}
          {...props}
        >
          {children}
        </Tag>
      </TypographyContext.Provider>
    )
  },
)
Typography.displayName = 'Typography'
